'use client'

import { Accordion, Box, Container, Heading, Image, Stack, Text } from '@doist/marketist'

import { addProtocol } from 'app/services/contentful/add-protocol-to-url'
import { isTypeTdCustomPagePrefooterTwoColumn } from 'app/services/contentful/models'
import { RenderRichText } from 'app/services/contentful/render-rich-text'
import { getPrefooterColors } from 'app/services/get-prefooter-colors'
import { ContainerTwoColumns } from 'components/container-two-column/container-two-column'
import { TextLockUp } from 'components/text-lock-up/text-lock-up'

import { CPButton } from '../button/button'
import { CustomerLogos } from '../customer-logos/customer-logos'

import css from './prefooter.module.css'

import type { ReactNode } from 'react'
import type { PrefooterFAQVariant, PrefooterTwoColumnVariant } from '../types'

import { isDefined } from '@/services/is-defined'

type PrefooterProps = {
    page: PrefooterFAQVariant | PrefooterTwoColumnVariant
    children: ReactNode
}

function Prefooter({ page, children }: PrefooterProps) {
    const { theme, title, button } = page.fields
    const bgColor = getPrefooterColors(theme).backgroundColor

    return (
        <Box className={css.prefooterBackground} style={{ '--background-color': bgColor }}>
            <Stack gap={{ xs: '96', md: '128' }}>
                <Container width="sm" className={css.prefooter}>
                    {title ? (
                        <Heading level="2" size="major-lg" className="mbe-48">
                            {title}
                        </Heading>
                    ) : null}
                    {button ? <CPButton {...button} /> : null}
                    {includeCustomerLogos(page) ? <CustomerLogos className="mbs-64" /> : null}
                </Container>
                {children}
            </Stack>
        </Box>
    )
}

function FaqPrefooter(props: PrefooterFAQVariant) {
    const { faqs = [], faqsTitle } = props.fields

    const items = faqs.filter(isDefined).map((faq) => ({
        question: faq.fields.question,
        answer: (
            <Text tag="div">
                <RenderRichText>{faq.fields.answer}</RenderRichText>
            </Text>
        ),
    }))

    return (
        <Prefooter page={props}>
            <Container width="sm" className={css.faq}>
                <Accordion useAsTemplate items={items} title={faqsTitle} />
            </Container>
        </Prefooter>
    )
}

function TwoColumnPrefooter(props: PrefooterTwoColumnVariant) {
    const {
        leftColumnTitle = '',
        leftColumnSubtitle,
        leftColumnButton,
        rightColumnImage,
    } = props.fields

    const imgSrc = addProtocol(rightColumnImage?.fields.file?.url)

    return (
        <Prefooter page={props}>
            <ContainerTwoColumns>
                <ContainerTwoColumns.BodyContainer className={css.textColumn}>
                    <TextLockUp
                        title={leftColumnTitle}
                        size="base"
                        {...(leftColumnButton
                            ? { buttons: <CPButton {...leftColumnButton} /> }
                            : {})}
                    >
                        {leftColumnSubtitle}
                    </TextLockUp>
                </ContainerTwoColumns.BodyContainer>

                {rightColumnImage && imgSrc ? (
                    <Box className={css.imageColumn}>
                        <Image
                            src={imgSrc}
                            alt={rightColumnImage.fields.title}
                            width={632}
                            height={360}
                        />
                    </Box>
                ) : null}
            </ContainerTwoColumns>
        </Prefooter>
    )
}

function includeCustomerLogos(props: PrefooterProps['page']) {
    return isTypeTdCustomPagePrefooterTwoColumn(props) && props.fields.includeCustomerLogos
}

export { FaqPrefooter, Prefooter, TwoColumnPrefooter }
